import { createBrowserRouter, Outlet } from 'react-router'
import { lazy, Suspense } from 'react'
import { registerSprintRoutes } from '@waago/api'
import { AuthProvider } from '../lib/auth'
import { AppShell } from '../components/AppShell'
import { RequireAuth } from './RequireAuth'
import { LoginPage } from './pages/LoginPage'
import { BACKOFFICE_SPRINTS } from './sprints'

// Installed before the router renders, so the first navigation already knows
// what this build is showing.
registerSprintRoutes(BACKOFFICE_SPRINTS)

// Route-level code splitting keeps the initial bundle within the PHASE-006
// budget (<= 250 KB gzipped).
const ForgotPassword = lazy(() =>
  import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
)
const ResetPassword = lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
)
const Dashboard = lazy(() => import('./pages/DashboardPage'))
const Clients = lazy(() => import('./pages/ClientsPage'))
const ClientDetail = lazy(() => import('./pages/ClientDetailPage'))
const Providers = lazy(() => import('./pages/ProvidersPage'))
const ProviderDetail = lazy(() => import('./pages/ProviderDetailPage'))
const Missions = lazy(() => import('./pages/MissionsPage'))
const MissionDetail = lazy(() => import('./pages/MissionDetailPage'))
const Reports = lazy(() => import('./pages/ReportsPage'))
const ReportDetail = lazy(() => import('./pages/ReportDetailPage'))
const Payments = lazy(() => import('./pages/PaymentsPage'))
const PaymentDetail = lazy(() => import('./pages/PaymentDetailPage'))
const Services = lazy(() => import('./pages/ServicesPage'))
const ServiceDetail = lazy(() => import('./pages/ServiceDetailPage'))
const VehicleTypes = lazy(() => import('./pages/VehicleTypesPage'))
const Subscriptions = lazy(() => import('./pages/SubscriptionsPage'))
const Campaigns = lazy(() => import('./pages/CampaignsPage'))
const CampaignDetail = lazy(() => import('./pages/CampaignDetailPage'))
const Settings = lazy(() => import('./pages/SettingsPage'))
const Notifications = lazy(() => import('./pages/NotificationsPage'))
const Account = lazy(() => import('./pages/AccountPage'))
const PlanEdit = lazy(() => import('./pages/PlanEditPage'))

function Loading() {
  return (
    <div className="animate-slide-up p-8 text-sm text-slate-500" role="status">
      Chargement…
    </div>
  )
}

/**
 * The one place the session lives. See the note on the client app's Root: a
 * provider mounted per route branch re-exchanges the refresh cookie on every
 * navigation between them, and two rotations of one token look like a stolen
 * one.
 */
function Root() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  )
}

function Shell() {
  return (
    <RequireAuth>
      <Suspense fallback={<Loading />}>
        <AppShell />
      </Suspense>
    </RequireAuth>
  )
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      {
        path: '/connexion',
        element: <LoginPage />,
      },
      {
        path: '/mot-de-passe-oublie',
        element: (
          <Suspense fallback={<Loading />}>
            <ForgotPassword />
          </Suspense>
        ),
      },
      {
        path: '/reinitialiser-mot-de-passe',
        element: (
          <Suspense fallback={<Loading />}>
            <ResetPassword />
          </Suspense>
        ),
      },
      {
        path: '/',
        element: <Shell />,
        children: [
          { index: true, element: <Dashboard /> },
          {
            path: 'clients',
            element: (
              <RequireAuth roles={['OPS', 'SUPPORT']}>
                <Clients />
              </RequireAuth>
            ),
          },
          {
            path: 'clients/:id',
            element: (
              <RequireAuth roles={['OPS', 'SUPPORT']}>
                <ClientDetail />
              </RequireAuth>
            ),
          },
          {
            path: 'prestataires',
            element: (
              <RequireAuth roles={['OPS', 'KYC_REVIEWER']}>
                <Providers />
              </RequireAuth>
            ),
          },
          {
            path: 'prestataires/:id',
            element: (
              <RequireAuth roles={['OPS', 'KYC_REVIEWER']}>
                <ProviderDetail />
              </RequireAuth>
            ),
          },
          {
            path: 'missions',
            element: (
              <RequireAuth roles={['OPS']}>
                <Missions />
              </RequireAuth>
            ),
          },
          {
            path: 'missions/:id',
            element: (
              <RequireAuth roles={['OPS']}>
                <MissionDetail />
              </RequireAuth>
            ),
          },
          {
            path: 'signalements',
            element: (
              <RequireAuth roles={['OPS', 'SUPPORT']}>
                <Reports />
              </RequireAuth>
            ),
          },
          {
            path: 'signalements/:id',
            element: (
              <RequireAuth roles={['OPS', 'SUPPORT']}>
                <ReportDetail />
              </RequireAuth>
            ),
          },
          {
            path: 'paiements',
            element: (
              <RequireAuth roles={['FINANCE', 'OPS']}>
                <Payments />
              </RequireAuth>
            ),
          },
          {
            path: 'paiements/:id',
            element: (
              <RequireAuth roles={['FINANCE', 'OPS']}>
                <PaymentDetail />
              </RequireAuth>
            ),
          },
          {
            path: 'services',
            element: (
              <RequireAuth roles={['OPS']}>
                <Services />
              </RequireAuth>
            ),
          },
          {
            path: 'services/:activity',
            element: (
              <RequireAuth roles={['OPS']}>
                <ServiceDetail />
              </RequireAuth>
            ),
          },
          {
            path: 'vehicules',
            element: (
              <RequireAuth roles={['OPS']}>
                <VehicleTypes />
              </RequireAuth>
            ),
          },
          {
            path: 'abonnements',
            element: (
              <RequireAuth roles={['FINANCE', 'MARKETING']}>
                <Subscriptions />
              </RequireAuth>
            ),
          },
          {
            // "Modifier abonnement" (frame `5072:11649`).
            path: 'abonnements/:code',
            element: (
              <RequireAuth roles={['FINANCE', 'MARKETING']}>
                <PlanEdit />
              </RequireAuth>
            ),
          },
          {
            path: 'publicite',
            element: (
              <RequireAuth roles={['MARKETING', 'OPS']}>
                <Campaigns />
              </RequireAuth>
            ),
          },
          {
            // "Detail publicité & visibilité" (frames `5081:17121`,
            // `5081:10973`, `5081:17277`) — one campaign, and the decision.
            path: 'publicite/:id',
            element: (
              <RequireAuth roles={['MARKETING', 'OPS']}>
                <CampaignDetail />
              </RequireAuth>
            ),
          },
          // Any signed-in staff member can change their own password; the
          // internal-user panel checks SUPER_ADMIN itself.
          { path: 'parametres', element: <Settings /> },
          // The header bell has pointed here since it was built; until now
          // there was no route behind it.
          { path: 'notifications', element: <Notifications /> },
          // "Mon compte" (frame `5081:18924`), behind the header avatar.
          { path: 'mon-compte', element: <Account /> },
        ],
      },
    ],
  },
])
